import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { brand, pageMeta } from "@/data/mockData";

// ============================================================================
//  ABOUT JOHNNY TAN
//  Content sourced verbatim from "JOHNNY ABOUT ME Chatgpt.docx".
//  Visual language matches the rest of the site (white + navy + gold accents).
// ============================================================================

// Client-provided studio + lifestyle photography, optimized → /public/johnny/.
// Spread across the page for face-branding ("insert my photo wherever possible").
const photos = {
  heroStanding: "/johnny/formal-3.jpg", // standing navy blazer — clean front pose
  seatedStool: "/johnny/formal-4.jpg", // seated on stool, relaxed
  seatedChair: "/johnny/formal-3.jpg", // seated in ornate chair, hands clasped
  casualPortrait: "/johnny/casual-portrait.jpg", // leaning, blue shirt — approachable
  casualWide: "/johnny/casual-wide.jpg", // wide leaning shot — full-bleed banner
};

const frameworks = [
  {
    code: "W.A.T.E.R",
    title: "The W.A.T.E.R\nConcept",
    desc: "Wealth Accumulation Towards Early Retirement — a long-term roadmap built around financial independence.",
    image: "/AboutMe_pagePics/Water Concept.png",
  },
  {
    code: "C.L.E.A.R",
    title: "The C.L.E.A.R\nFramework",
    desc: "A structured five-step process that converts complex property decisions into sustainable plans.",
    image: "/AboutMe_pagePics/clear framework.png",
  },
  {
    code: "L.E.X",
    title: "The L.E.X\nMethod",
    desc: "Location, Entry Price, Exit Strategy — three filters that minimise risk before you maximise upside.",
    image: "/AboutMe_pagePics/LEX method.png",
  },
  {
    code: "M.E",
    title: "The M.E\nRoadmap",
    desc: "The MeProperty positioning philosophy — clarity, structure, and intent applied to every property move.",
    image: "/AboutMe_pagePics/me road map.png",
  },
];

const philosophyPoints = [
  "Financial clarity",
  "Market positioning",
  "Affordability sustainability",
  "Future exit planning",
  "Wealth accumulation",
  "Retirement-focused property strategies",
];

// Premium illustrated icons (reusing the existing advisory/services icon set so
// this section matches the rest of the site). Swap these paths when the client
// supplies their own exact icon files.
const expertise = [
  { title: "Singapore HDB Resale Advisory", desc: "Strategic advice to sell your HDB for the best value and smooth transition.", png: "/advisory-icons/Singapore HDB Resale Advisory.png", alt: "Singapore HDB resale advisory strategy icon" },
  { title: "HDB Upgrading Strategies", desc: "Step-by-step upgrading strategies that maximise value and minimise risk.", png: "/advisory-icons/HDB Upgrading Strategies.png", alt: "HDB to condo upgrading planning strategy icon" },
  { title: "Condo Purchase Planning", desc: "Data-driven planning to find the right condo, at the right price, in the right location.", png: "/advisory-icons/Condo Purchase Planning.png", alt: "Singapore condo purchase planning advisory icon" },
  { title: "Property Investment Advisory", desc: "Build a robust property portfolio with high-performing assets and long-term returns.", png: "/advisory-icons/Property Investment Advisory.png", alt: "Singapore property investment advisory icon" },
  { title: "Asset Progression Planning", desc: "Plan your property roadmap to grow from one asset to multiple assets.", png: "/advisory-icons/Asset Progression Planning.png", alt: "Property asset progression wealth strategy icon" },
  { title: "Exit Strategy Planning", desc: "Clear exit strategies tailored to lock in gains and achieve your financial goals.", png: "/advisory-icons/Exit Strategy Planning.png", alt: "Property exit strategy planning icon" },
  { title: "Retirement-Focused Restructuring", desc: "Restructure your property portfolio to generate income and support your ideal retirement.", png: "/advisory-icons/Retirement-Focused Restructuring.png", alt: "Retirement focused property restructuring icon" },
  { title: "Long-Term Property Wealth Accumulation", desc: "Sustainable wealth creation through property for lasting financial freedom.", png: "/advisory-icons/Long-Term Property Wealth Accumulation.png", alt: "Long-term property wealth accumulation icon" },
];

const marketGuidance = [
  "HDB MOP timelines and upgrade opportunities",
  "Seller's Stamp Duty (SSD) considerations",
  "Entry price versus future exit positioning",
  "Rental yield and passive income planning",
  "Capital appreciation potential",
  "Asset restructuring opportunities",
  "Property portfolio growth strategies",
];

const scenarios = [
  { img: "/AboutMe_Approachable-icons/purchasing their First hone.png", title: "Purchasing their first home" },
  { img: "/AboutMe_Approachable-icons/upgrade from hdb to condo.png", title: "Upgrading from HDB to condo" },
  { img: "/AboutMe_Approachable-icons/exploring investment.png", title: "Exploring investment opportunities" },
  { img: "/AboutMe_Approachable-icons/Restructuring.png", title: "Restructuring their property portfolio" },
  { img: "/AboutMe_Approachable-icons/planning for retiremnt.png", title: "Planning for retirement" },
];

const beyond = [
  "Stronger financial foundations",
  "Future flexibility",
  "Sustainable wealth",
  "Greater freedom of choice over time",
];

const Eyebrow = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold">
    {children}
  </div>
);

export default function About() {
  return (
    <>
      <SEO {...pageMeta.about} />

      {/* =================== 1. HERO =================== */}
      <section className="bg-white pt-16 pb-12 md:py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow>About Johnny Tan</Eyebrow>
            <h1 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Strategic Property Wealth Planning Through <span className="italic">Clarity, Structure &amp; Long-Term Vision</span>.
            </h1>
            <p className="mt-5 text-base md:text-lg font-serif italic text-[#0a2540]/80 leading-snug">
              Helping Singapore families make smarter property decisions since 2012.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {brand.positioning.split("|").map((part, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-[#fffbea] border border-[#ffde59]/40 text-[#0a2540] px-3 py-1.5 rounded-full text-xs font-medium"
                >
                  {part.trim()}
                </span>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="about-schedule-btn"
                className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-7 py-4 font-semibold text-sm rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Book Strategy Consultation
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <Link
                to="/framework"
                className="inline-flex items-center gap-2 border border-[#0a2540]/20 text-[#0a2540] px-7 py-4 font-medium text-sm rounded-full hover:border-[#0a2540] transition-all"
              >
                Explore Framework
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-4 bg-[#fffbea] rounded-3xl -z-10" />
              <img
                src={photos.heroStanding}
                alt="Johnny Tan, Singapore Property Wealth Advisor"
                loading="eager"
                className="relative w-full object-cover rounded-3xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-gray-100 shadow-xl px-5 py-4 rounded-2xl max-w-[230px]">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                  Since 2012
                </div>
                <div className="font-serif text-[#0a2540] text-lg leading-tight mt-0.5">
                  Johnny Tan · 陈健盛
                </div>
                <div className="text-[11px] text-slate-500 mt-1">CEA {brand.cea}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =================== 2. INTRODUCTION =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-introduction">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.3fr,1fr] gap-14 items-start">
          <Reveal>
            <Eyebrow>Introduction</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1] mb-8">
              Property is <span className="italic">more than</span> just buying and selling.
            </h2>
            <div className="space-y-5 text-base md:text-lg text-slate-700 leading-relaxed">
              <p>
                At MeProperty, Johnny Tan believes that property is more than just buying and selling. When approached strategically, real estate becomes a powerful tool for financial flexibility, lifestyle progression, retirement readiness, and long-term wealth creation.
              </p>
              <p>
                Since entering Singapore's real estate industry in 2012, Johnny has been helping homeowners, HDB upgraders, investors, and families navigate the property market with greater clarity, confidence, and structure.
              </p>
              <p>
                His advisory approach is built around understanding not just the property — but the person, the life stage, the financial position, and the long-term direction behind every move.
              </p>
              <p className="font-medium text-[#0a2540]">
                Because every property decision made today can influence future opportunities for years to come.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative space-y-6">
              <div className="relative">
                <div className="absolute -inset-3 bg-[#fffbea] rounded-3xl -z-10" />
                <img
                  src={photos.seatedStool}
                  alt="Johnny Tan, Singapore property advisor"
                  loading="lazy"
                  className="relative w-full object-cover rounded-3xl"
                />
              </div>
              <div className="bg-white border border-gray-100 rounded-3xl p-7 md:p-9 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0a2540]/60 font-semibold mb-4">
                  Property Advisor
                </div>
                <div className="font-serif text-2xl text-[#0a2540] leading-tight">
                  Johnny Tan · 陈健盛
                </div>
                <div className="mt-1.5 text-sm text-[#0a2540]/80 font-semibold">
                  Singapore Property Wealth Advisor
                </div>
                <div className="h-px my-6 bg-gradient-to-r from-[#ffde59] to-transparent" />
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#023e8a] text-xs" aria-hidden="true" />
                    Active in Singapore real estate since 2012
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#023e8a] text-xs" aria-hidden="true" />
                    280+ transactions handled
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#023e8a] text-xs" aria-hidden="true" />
                    CEA Registration {brand.cea}
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#023e8a] text-xs" aria-hidden="true" />
                    Creator of the C.L.A.R.I.T.Y Property System
                  </li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =================== 3. STRUCTURED ADVISORY APPROACH =================== */}
      <section className="bg-white py-20 md:py-28" data-testid="about-system">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-4xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#cfa94a] font-semibold flex flex-col items-center gap-2 mb-4">
              A STRUCTURED & STRATEGIC ADVISORY APPROACH
              <span className="text-[8px]">♦</span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.15]">
              Johnny is the creator of the <br />
              <span className="italic">C.L.A.R.I.T.Y</span> Property System<span className="text-[#cfa94a]">.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              A structured property wealth planning approach that integrates:
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {frameworks.map((f, i) => (
              <motion.div
                key={f.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="clarity-card group bg-white border border-gray-100 rounded-3xl p-7 text-center"
              >
                {f.image ? (
                  <img
                    src={f.image}
                    alt={f.title}
                    loading="lazy"
                    className="clarity-icon w-full h-40 object-contain mx-auto mb-6"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-[#fffbea] inline-flex items-center justify-center mb-6 mx-auto">
                    <i className={`${f.icon} text-[#023e8a] text-lg`} aria-hidden="true" />
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#cfa94a] font-semibold mb-3">
                  {f.code}
                </div>
                <h3 className="font-serif text-xl sm:text-2xl text-[#0a2540] leading-tight whitespace-pre-line">{f.title}</h3>
                <div className="clarity-rule h-px w-4 mx-auto bg-[#cfa94a] my-4" />
                <p className="text-sm text-slate-500 leading-relaxed px-2">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-base md:text-lg text-slate-700 leading-relaxed">
              Together, these frameworks help clients make more informed and sustainable property decisions through strategic planning, risk management, asset progression, and long-term positioning.
            </p>
            <Link
              to="/framework"
              data-testid="about-explore-framework"
              className="mt-7 inline-flex items-center gap-2 text-[#0a2540] font-medium link-underline"
            >
              Explore the full framework
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* =================== WIDE BANNER (face branding) =================== */}
      <section className="relative overflow-hidden" data-testid="about-banner">
        <img
          src={photos.casualWide}
          alt="Johnny Tan — Singapore property wealth advisor"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-left-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540]/10 via-[#0a2540]/45 to-[#0a2540]/90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 lg:py-44">
          <div className="ml-auto max-w-xl text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#ffde59] font-semibold mb-4">
              Personalised. Strategic. Long-Term.
            </div>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white leading-snug">
              Every great property decision begins with a{" "}
              <span className="italic">conversation</span>.
            </p>
          </div>
        </div>
      </section>

      {/* =================== 4. ADVISORY PHILOSOPHY =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-philosophy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr,1.2fr] gap-14 items-center">
            <Reveal>
              <Eyebrow>Advisory Philosophy</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
                Beyond transactions. <span className="italic">Toward strategy.</span>
              </h2>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                Rather than focusing purely on transactions, Johnny's approach centers around:
              </p>
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {philosophyPoints.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3.5 text-sm text-slate-700"
                  >
                    <i className="fa-solid fa-circle-check text-[#023e8a]" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.15} className="space-y-6">
              <div className="relative">
                <div className="absolute -inset-3 bg-white rounded-3xl -z-10" />
                <img
                  src={photos.seatedChair}
                  alt="Johnny Tan advising on property wealth strategy"
                  loading="lazy"
                  className="relative w-full object-cover rounded-3xl"
                />
              </div>
              <div className="relative bg-white border border-[#ffde59]/40 rounded-3xl p-8 md:p-10 shadow-sm">
                <i
                  className="fa-solid fa-quote-left text-5xl"
                  style={{ color: "#ffde59" }}
                  aria-hidden="true"
                />
                <p className="mt-5 font-serif text-xl md:text-2xl lg:text-3xl text-[#0a2540] leading-snug">
                  Property decisions should not be driven by fear, hype, or emotion alone.{" "}
                  <span className="italic">They should be guided by structure, timing, and long-term purpose.</span>
                </p>
                <div className="mt-7 pt-5 border-t border-gray-100 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#fffbea] inline-flex items-center justify-center">
                    <i className="fa-solid fa-feather text-[#023e8a]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0a2540]">Johnny Tan</div>
                    <div className="text-xs text-slate-500">Singapore Property Wealth Advisor</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* =================== 5. AREAS OF EXPERTISE =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-expertise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-4xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#cfa94a] font-semibold flex flex-col items-center gap-2 mb-4">
              AREAS OF EXPERTISE
              <span className="text-[8px]">♦</span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.15]">
              Johnny specialises in<span className="text-[#cfa94a]">:</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {expertise.map((e, i) => (
              <motion.div
                key={e.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                className="advisory-card bg-white border border-gray-100 rounded-2xl p-7 text-center shadow-sm"
                data-testid={`expertise-${i}`}
              >
                <picture>
                  <img
                    src={e.png}
                    alt={e.alt || `${e.title} icon`}
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                    className="advisory-icon h-24 sm:h-28 max-w-full object-contain mb-6 mx-auto select-none drop-shadow-sm"
                  />
                </picture>
                <h3 className="font-serif text-[17px] sm:text-lg text-[#0a2540] leading-tight mb-3 px-1">{e.title}</h3>
                <p className="text-[13px] sm:text-sm text-slate-500 leading-relaxed px-1">{e.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== 6. MARKET GUIDANCE =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-guidance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Market Guidance</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              What Johnny <span className="italic">helps clients understand:</span>
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Over the years, he has guided clients through different phases of the Singapore property market with a focus on:
            </p>
          </Reveal>

          <div className="max-w-3xl mx-auto">
            <ol className="space-y-4">
              {marketGuidance.map((g, i) => (
                <motion.li
                  key={g}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="flex items-center gap-5 bg-white border border-gray-100 rounded-2xl p-5 md:p-6 hover:shadow-md transition-shadow"
                >
                  <div className="shrink-0 h-10 w-10 rounded-full bg-[#0a2540] text-[#ffde59] inline-flex items-center justify-center font-serif font-bold text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <span className="text-sm md:text-base text-slate-700 leading-relaxed">{g}</span>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* =================== 7. CONSULTATIVE & EDUCATIONAL STYLE =================== */}
      <section className="bg-white py-20 md:py-28" data-testid="about-style">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Consultative &amp; Educational Style</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Approachable. <span className="italic">Structured. Patient.</span>
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-[0.85fr,1.15fr] gap-12 lg:gap-16 items-center mb-16">
            <Reveal>
              <div className="relative max-w-sm mx-auto">
                <div className="absolute -inset-4 bg-[#fafafa] rounded-3xl -z-10" />
                <img
                  src={photos.casualPortrait}
                  alt="Johnny Tan in a relaxed advisory setting"
                  loading="lazy"
                  className="relative w-full object-cover rounded-3xl"
                />
              </div>
            </Reveal>
            <div className="space-y-5 text-base md:text-lg text-slate-700 leading-relaxed">
              <Reveal delay={0.05}>
                <p>
                  Clients often describe Johnny as approachable, structured, and patient in his advisory process. He believes that meaningful property planning starts with listening and understanding before recommending any strategy.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p>
                  Every client's situation is different. Every life stage carries different priorities. Every property move should therefore be approached with a customised roadmap.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mb-10">
            <div className="flex items-center w-full max-w-xl mx-auto mb-4">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent to-[#ffde59]"></div>
              <div className="text-[10px] text-[#cfa94a] px-3">♦</div>
              <div className="flex-grow h-px bg-gradient-to-l from-transparent to-[#ffde59]"></div>
            </div>
            <div className="text-sm uppercase tracking-[0.22em] text-[#0a2540] font-semibold">
              WHETHER SOMEONE IS
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 pb-8">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="advisory-card bg-white border border-gray-100 rounded-2xl p-5 md:p-6 text-center shadow-sm"
              >
                <picture>
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="advisory-icon h-28 max-w-full object-contain mb-5 mx-auto select-none"
                  />
                </picture>
                <p className="text-sm text-[#0a2540] font-semibold leading-snug px-1">{s.title}</p>
                <div className="h-[2px] w-6 mx-auto bg-[#ffde59] mt-4" />
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-12 relative z-10 max-w-5xl mx-auto">
            <img 
              src="/AboutMe_Approachable-icons/bottom pic.png" 
              alt="Johnny focuses on providing practical, data-backed, and personalised guidance tailored to the client's long-term objectives." 
              className="w-full object-contain drop-shadow-md" 
            />
          </Reveal>
        </div>
      </section>

      {/* =================== 8. BEYOND TRANSACTIONS =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-beyond">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-14 items-start">
            <Reveal>
              <Eyebrow>Beyond Transactions</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
                Not the next hot property. <span className="italic">It is building the life you want with the right decision.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed">
                At the core of Johnny's philosophy is the belief that successful real estate planning is not about chasing the next hot property. It is about building:
              </p>
              <ul className="mt-7 space-y-3">
                {beyond.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-base text-slate-700">
                    <i className="fa-solid fa-circle-check text-[#023e8a]" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative bg-white border border-[#ffde59]/40 rounded-3xl p-8 md:p-10 shadow-sm">
                <i
                  className="fa-solid fa-quote-left text-5xl"
                  style={{ color: "#ffde59" }}
                  aria-hidden="true"
                />
                <p className="mt-5 font-serif text-xl md:text-2xl lg:text-3xl text-[#0a2540] leading-snug">
                  A property journey is not just about the next purchase.{" "}
                  <span className="italic">It is about building the life you want through the right decisions today.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* =================== 9. PROFILE SIGNATURE =================== */}
      <section className="bg-white py-20 md:py-24" data-testid="about-signature">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <img
              src={brand.logoUrl}
              alt="MeProperty mark"
              loading="lazy"
              className="h-16 w-16 rounded-full object-cover mx-auto mb-7"
            />
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0a2540] leading-tight">
              Johnny Tan. <span className="font-serif">陈健盛</span>
            </h2>
            <div className="mt-4 text-xs sm:text-sm uppercase tracking-[0.28em] text-[#0a2540]/80 font-semibold">
              Singapore Property Wealth Advisor
            </div>
            <div className="h-px my-7 max-w-xs mx-auto bg-gradient-to-r from-transparent via-[#ffde59] to-transparent" />
            <div className="flex flex-wrap justify-center gap-2">
              {brand.positioning.split("|").map((part, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-[#fffbea] border border-[#ffde59]/40 text-[#0a2540] px-3 py-1.5 rounded-full text-xs font-medium"
                >
                  {part.trim()}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* =================== 10. FINAL CTA =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="about-final-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-white border border-[#ffde59]/40 rounded-3xl p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#fffbea] blur-3xl pointer-events-none" />
            <div className="relative text-center max-w-3xl mx-auto">
              <Eyebrow>Your Next Move</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.05]">
                Ready to plan your next property move with <span className="italic">clarity?</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
                Book a private consultation and receive structured, data-backed property planning tailored to your goals.
              </p>
              <div className="mt-9">
                <Link
                  to="/contact"
                  data-testid="about-final-cta-btn"
                  className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-8 py-4 font-semibold text-base rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Book Strategy Consultation
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
